import { getFontValue } from '@vedalib/editor/lib/brand'
import * as R from 'ramda'
import React, { useCallback, useMemo } from 'react'

import { TileButton } from 'components/uiKit/Button'
import { COMPANY_PATHS } from 'constants/paths'
import { useFontsGroupAll } from 'gql/fontsGroup/apollo'
import { usePathParams } from 'routes/hooks'
import { FONT_WEIGHT_OPTIONS } from 'utils/font'

import type { ElementFontValue } from '../../types'
import TagEdited from '../BrandTagEdited'

const WEIGHT_MAP = R.map(R.prop('label'), R.indexBy(R.prop('value'), FONT_WEIGHT_OPTIONS))
const OMIT_STYLE_KEYS = ['fontSize', 'lineHeight', 'color'] as const

interface IBrandButtonFontProps<S extends string> {
  onClick: (font: S) => void
  font: S
  styles: React.CSSProperties
  values: ElementFontValue[string]
  edited?: boolean
  children?: React.ReactNode
}

const BrandButtonFont = <S extends string>({
  onClick,
  edited,
  styles,
  values,
  font,
  children,
}: IBrandButtonFontProps<S>) => {
  const { companyId } = usePathParams(COMPANY_PATHS.main)
  const { data } = useFontsGroupAll({ companyId })

  const fontFamilyName = useMemo(() => {
    const ff = getFontValue(String(values.fontFamily))
    if (ff?.id) {
      const fontGroup = data?.data.find(({ id }) => id === ff.id)
      return fontGroup?.name
    }
    return ff?.name
  }, [data?.data, values?.fontFamily])

  const handleClick = useCallback(() => onClick(font), [font, onClick])

  return (
    <TileButton
      description={`${fontFamilyName} ${String(WEIGHT_MAP[Number(styles.fontWeight)])}, ${styles?.fontSize}`}
      icon='otherRight'
      name='font'
      onClick={handleClick}
    >
      <span style={R.omit(OMIT_STYLE_KEYS, styles)}>{children}</span>
      {edited && <TagEdited />}
    </TileButton>
  )
}

export default BrandButtonFont
